import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { SourceBadges } from '../components/SourceBadges';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { Separator } from '../components/ui/separator';
import { Plus, LayoutDashboard, FileText, Trash2, Pencil, Building2, LogIn, Settings, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';

export default function ClientsPage({ navigate }) {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [sourceMap, setSourceMap] = useState({});
  const [sourceError, setSourceError] = useState('');
  const [sourceLoading, setSourceLoading] = useState(true);
  const [sourceRefresh, setSourceRefresh] = useState(0);
  const isInternal = ['super_admin', 'internal'].includes(user?.role) && !user?.impersonating;
  useEffect(() => {
    if (!isInternal) return;
    let active = true;
    const load = () => api('/api/data-sources/clients').then(result => {
      if (active) { setSourceMap(Object.fromEntries(result.clients.map(c => [c.client_id, c]))); setSourceError(''); }
    }).catch(e => { if (active) setSourceError(e.message); }).finally(()=>{if(active)setSourceLoading(false);});
    load();
    const timer = setInterval(load,15000);
    return () => { active = false; clearInterval(timer); };
  }, [isInternal,sourceRefresh]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncingId, setSyncingId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [form, setForm] = useState({
    name: '', industry: '', contact_email: '', logo_url: '',
    primary_color: '#1f2a4d', accent_color: '#e01e25',
    platforms: ['facebook', 'instagram', 'linkedin', 'tiktok', 'youtube']
  });

  const fetchClients = async () => {
    try {
      setLoading(true);
      const data = await api('/api/clients');
      setClients(data || []);
    } catch (err) {
      console.error('Failed to load clients:', err);
      toast.error('Failed to load clients');
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchClients(); }, []);

  const handleSave = async () => {
    try {
      if (editClient) {
        await api(`/api/clients/${editClient._id}`, {
          method: 'PUT', body: JSON.stringify(form)
        });
        toast.success('Client updated');
      } else {
        await api('/api/clients', {
          method: 'POST', body: JSON.stringify(form)
        });
        toast.success('Client created');
      }
      setDialogOpen(false);
      setEditClient(null);
      setForm({ name: '', industry: '', contact_email: '', logo_url: '', primary_color: '#1f2a4d', accent_color: '#e01e25', platforms: ['facebook', 'instagram', 'linkedin', 'tiktok', 'youtube'] });
      fetchClients();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this client?')) return;
    try {
      await api(`/api/clients/${id}`, { method: 'DELETE' });
      toast.success('Client deleted');
      fetchClients();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleLoginAs = async (clientId, clientName) => {
    try {
      const data = await api(`/api/auth/impersonate/${clientId}`, { method: 'POST' });
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      localStorage.setItem('admin_token', localStorage.getItem('token'));
      localStorage.setItem('admin_user', localStorage.getItem('user'));
      toast.success(`Viewing as ${clientName} client`);
      window.location.reload();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const openEdit = (client) => {
    setEditClient(client);
    setForm({
      name: client.name, industry: client.industry || '', contact_email: client.contact_email || '',
      logo_url: client.logo_url || '', primary_color: client.primary_color || '#1f2a4d',
      accent_color: client.accent_color || '#e01e25',
      platforms: client.platforms || ['facebook', 'instagram', 'linkedin', 'tiktok', 'youtube']
    });
    setDialogOpen(true);
  };

  const handleSyncAll = async () => {
    setSyncing(true);
    toast.info('Syncing all clients from Metricool. Monitor progress on the Metricool page.', { duration: 5000 });
    try {
      await api('/api/sync/all', { method:'POST', body:JSON.stringify({mode:'history'}) });
      toast.info('Metricool sync queued. Open Sync & Coverage to follow progress.', { duration:8000, action:{label:'View progress',onClick:()=>navigate('metricool')} });
    } catch (err) {
      toast.error('Sync failed: ' + err.message);
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncClient = async (clientId, clientName) => {
    setSyncingId(clientId);
    toast.info(`Syncing ${clientName}...`, { duration: 3000 });
    try {
      await api(`/api/sync/client/${clientId}`, { method:'POST' });
      toast.info(`${clientName}: Metricool sync queued.`, {duration:8000, action:{label:'View progress',onClick:()=>navigate('metricool')}});
    } catch (err) {
      toast.error(`${clientName}: ${err.message}`);
    } finally {
      setSyncingId(null);
    }
  };

  const openCreate = () => {
    setEditClient(null);
    setForm({ name: '', industry: '', contact_email: '', logo_url: '', primary_color: '#1f2a4d', accent_color: '#e01e25', platforms: ['facebook', 'instagram', 'linkedin', 'tiktok', 'youtube'] });
    setDialogOpen(true);
  };

  const platformToggle = (platform) => {
    setForm(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platform)
        ? prev.platforms.filter(p => p !== platform)
        : [...prev.platforms, platform]
    }));
  };

  return (
    <div className="animate-fade-in-up">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Clients
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage your client accounts and reporting configurations
          </p>
        </div>
        <div className="flex gap-2">
          {user?.role === 'super_admin' && (
            <Button variant="outline" onClick={handleSyncAll} disabled={syncing} data-testid="sync-all-button">
              <RefreshCw size={16} className={`mr-2 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync All Data'}
            </Button>
          )}
          <Button onClick={openCreate} data-testid="clients-create-button" style={{ background: '#1f2a4d' }}>
            <Plus size={16} className="mr-2" /> New Client
          </Button>
        </div>
      </div>

      {isInternal && <div className="mb-5 rounded-xl border bg-primary/5 px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="clients-source-explanation"><p className="text-xs text-muted-foreground">Source and sync status are shown below each client. Metricool supplies connected organic and Facebook Ads reporting; Google Ads and Beehiiv keep their direct connections.</p><Button variant="outline" className="min-h-11 shrink-0" onClick={() => navigate('sources')} data-testid="clients-open-source-audit">Check connections</Button></div>}
      {sourceError && isInternal && <div className="text-sm text-destructive mb-4" data-testid="clients-source-error">Source information unavailable: {sourceError}<Button variant="outline" className="ml-2 min-h-11" onClick={()=>{setSourceLoading(true);setSourceRefresh(v=>v+1);}} data-testid="clients-source-retry">Retry</Button></div>}

      {/* Client Cards Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[1,2,3].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6"><div className="h-24 bg-muted rounded"></div></CardContent>
            </Card>
          ))}
        </div>
      ) : clients.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Building2 size={40} className="mx-auto text-muted-foreground/40 mb-4" />
            <h3 className="text-lg font-medium mb-2">No clients yet</h3>
            <p className="text-sm text-muted-foreground mb-4">Create your first client to start generating reports</p>
            <Button onClick={openCreate} style={{ background: '#1f2a4d' }}>
              <Plus size={16} className="mr-2" /> Create Client
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 items-start" data-testid="clients-table">
          {clients.map((client) => (
            <Card key={client._id} className="metric-card-hover group min-w-0" data-testid={`client-card-${client._id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between gap-2 mb-4">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-lg"
                      style={{ background: client.primary_color || '#1f2a4d' }}
                    >
                      {client.name[0]}
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-base break-words" data-testid={`client-name-${client._id}`}>{client.name}</h3>
                      {client.industry && (
                        <span className="text-xs text-muted-foreground">{client.industry}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); openEdit(client); }}>
                      <Pencil size={14} />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={(e) => { e.stopPropagation(); handleDelete(client._id); }}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>

                <div className="flex flex-wrap gap-1 mb-4">
                  {(client.platforms || []).map(p => {
                    const colors = { facebook: '#1877F2', instagram: '#E4405F', linkedin: '#0A66C2', tiktok: '#000', youtube: '#FF0000' };
                    return (
                      <span key={p} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium text-white" style={{ background: colors[p] || '#666' }}>
                        {p.charAt(0).toUpperCase() + p.slice(1)}
                      </span>
                    );
                  })}
                  {(client.paid_services || []).includes('facebook_ads') && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium bg-blue-100 text-blue-800 border border-blue-200">
                      FB Ads
                    </span>
                  )}
                  {(client.paid_services || []).includes('google_ads') && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium bg-green-100 text-green-800 border border-green-200">
                      Google Ads
                    </span>
                  )}
                  {client.beehiiv_enabled && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium bg-orange-100 text-orange-800 border border-orange-200">
                      Beehiiv
                    </span>
                  )}
                  {(client.platforms || []).length === 0 && !(client.paid_services || []).length && !client.beehiiv_enabled && (
                    <span className="text-[10px] text-muted-foreground italic">No services configured</span>
                  )}
                </div>

                <div className="mb-4">{isInternal&&sourceLoading&&!sourceMap[client._id]?<div className="space-y-2" data-testid={`client-${client._id}-source-loading`}><Skeleton className="h-6 w-full"/><p className="text-xs text-muted-foreground">Checking reporting sources…</p></div>:<SourceBadges info={sourceMap[client._id]} compact details testPrefix={`client-${client._id}-source`}/>}</div>
                <Separator className="mb-4" />

                <div className="flex gap-2">
                  <Button
                    size="sm" variant="outline" className="flex-1 text-xs"
                    onClick={() => navigate('dashboard', { clientId: client._id })}
                    data-testid={`client-dashboard-${client._id}`}
                  >
                    <LayoutDashboard size={14} className="mr-1" /> Dashboard
                  </Button>
                  <Button
                    size="sm" variant="outline" className="flex-1 text-xs"
                    onClick={() => navigate('reports', { clientId: client._id })}
                    data-testid={`client-reports-${client._id}`}
                  >
                    <FileText size={14} className="mr-1" /> Reports
                  </Button>
                </div>
                <div className="flex gap-2 mt-2">
                  <Button
                    size="sm" variant="outline" className="flex-1 text-xs"
                    onClick={(e) => { e.stopPropagation(); navigate('client-detail', { clientId: client._id }); }}
                    data-testid={`client-settings-${client._id}`}
                  >
                    <Settings size={14} className="mr-1" /> Configure
                  </Button>
                  {user?.role === 'super_admin' && (
                    <Button
                      size="sm" className="flex-1 text-xs text-white"
                      style={{ background: '#e01e25' }}
                      onClick={(e) => { e.stopPropagation(); handleLoginAs(client._id, client.name); }}
                      data-testid={`client-login-as-${client._id}`}
                    >
                      <LogIn size={14} className="mr-1" /> Login As
                    </Button>
                  )}
                </div>
                {user?.role === 'super_admin' && (
                  <Button
                    size="sm" variant="ghost" className="w-full mt-2 text-xs text-muted-foreground"
                    onClick={(e) => { e.stopPropagation(); handleSyncClient(client._id, client.name); }}
                    disabled={syncingId === client._id}
                    data-testid={`client-sync-${client._id}`}
                  >
                    <RefreshCw size={12} className={`mr-1 ${syncingId === client._id ? 'animate-spin' : ''}`} />
                    {syncingId === client._id ? 'Syncing...' : 'Sync Data'}
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editClient ? 'Edit Client' : 'Create New Client'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Client Name *</Label>
              <Input
                data-testid="client-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Turn Automotive"
              />
            </div>
            <div className="space-y-2">
              <Label>Industry</Label>
              <Input
                value={form.industry}
                onChange={(e) => setForm({ ...form, industry: e.target.value })}
                placeholder="e.g. Automotive, SaaS, Healthcare"
              />
            </div>
            <div className="space-y-2">
              <Label>Contact Email</Label>
              <Input
                type="email"
                value={form.contact_email}
                onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                placeholder="client@company.com"
              />
              <p className="text-[10px] text-muted-foreground">Used for report notifications and communications</p>
            </div>
            <div className="space-y-2">
              <Label>Logo URL</Label>
              <Input
                value={form.logo_url}
                onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                placeholder="https://..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Primary Color</Label>
                <div className="flex gap-2 items-center">
                  <input
                    type="color"
                    value={form.primary_color}
                    onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                    className="w-8 h-8 rounded cursor-pointer"
                  />
                  <Input value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="flex-1" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Accent Color</Label>
                <div className="flex gap-2 items-center">
                  <input
                    type="color"
                    value={form.accent_color}
                    onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                    className="w-8 h-8 rounded cursor-pointer"
                  />
                  <Input value={form.accent_color} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} className="flex-1" />
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <Label>Connected Services</Label>
              <p className="text-xs text-muted-foreground">Enable only the platforms this client uses — disabled platforms won't appear in dashboards or reports</p>
              <div className="space-y-2 mt-2">
                {[
                  { key: 'facebook', label: 'Facebook', color: '#1877F2', desc: 'Pages, posts, engagement' },
                  { key: 'instagram', label: 'Instagram', color: '#E4405F', desc: 'Reels, stories, feed' },
                  { key: 'linkedin', label: 'LinkedIn', color: '#0A66C2', desc: 'Company page, posts' },
                  { key: 'tiktok', label: 'TikTok', color: '#000000', desc: 'Videos, views, engagement' },
                  { key: 'youtube', label: 'YouTube', color: '#FF0000', desc: 'Videos, shorts, subscribers' },
                ].map(p => {
                  const enabled = form.platforms.includes(p.key);
                  return (
                    <div
                      key={p.key}
                      onClick={() => platformToggle(p.key)}
                      className={`flex items-center justify-between p-3 rounded-xl border-2 cursor-pointer transition-all ${
                        enabled ? 'border-[#1f2a4d] bg-[#1f2a4d]/5' : 'border-transparent bg-muted/30 hover:bg-muted/50'
                      }`}
                      data-testid={`platform-toggle-${p.key}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold" style={{ background: enabled ? p.color : '#ccc' }}>
                          {p.label[0]}
                        </div>
                        <div>
                          <div className="text-sm font-medium">{p.label}</div>
                          <div className="text-xs text-muted-foreground">{p.desc}</div>
                        </div>
                      </div>
                      <div className={`w-10 h-6 rounded-full transition-colors flex items-center ${enabled ? 'bg-[#1f2a4d] justify-end' : 'bg-gray-300 justify-start'}`}>
                        <div className="w-4 h-4 rounded-full bg-white mx-1 shadow-sm" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={!form.name} data-testid="client-save-button" style={{ background: '#1f2a4d' }}>
              {editClient ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
